/**
 * THE TREASURE PANEL — where the grocer edits the pool, and sees only counts.
 *
 * Privacy, deliberately: this panel can show how many times a treasure was
 * shown and added ACROSS the organization, and nothing else. No household, no
 * name, no list. A cell covering fewer than five households is withheld rather
 * than rounded, because rounding a count of two is still a count of two.
 */
import { Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import api from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';

const blank = () => ({ id: `t${Date.now()}`, catalog_id: '', name: '', note: '',
    enabled: true, sponsored: false, starts_on: '', ends_on: '' });

export default function GroceryTreasurePanel({ organizationId }) {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [pool, setPool] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (!organizationId) return;
        try {
            const r = await api.get('/grocery/admin/treasure', { params: { organization_id: organizationId } });
            setData(r.data);
            setPool(r.data.pool || []);
        } catch (_e) { setData(null); }
    }, [organizationId]);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setBusy(true);
        try {
            const r = await api.put('/grocery/admin/treasure',
                { pool, enabled: data?.enabled !== false, rotation: data?.rotation || 'daily' },
                { params: { organization_id: organizationId } });
            setData(r.data);
            setPool(r.data.pool || []);
            toast.success(t('grocery.cc.treasureSaved'));
        } catch (_e) { toast.error(t('common.error', 'Something went wrong')); } finally { setBusy(false); }
    };

    const patch = (i, k, v) => setPool((p) => p.map((row, j) => (j === i ? { ...row, [k]: v } : row)));
    const statFor = (id) => (data?.stats?.rows || []).find((r) => r.treasure_id === id);

    if (!organizationId) return null;

    return (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700/50 bg-white dark:bg-slate-900/40 p-4 space-y-3"
            data-testid="grocery-cc-treasure">
            <div>
                <p className="text-sm font-bold text-foreground">{t('grocery.cc.treasure')}</p>
                <p className="text-xs text-muted-foreground">{t('grocery.cc.treasureWhy')}</p>
            </div>

            <div className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground">{t('grocery.cc.treasureRotation')}</span>
                {['daily', 'weekly'].map((r) => (
                    <button key={r} onClick={() => setData((d) => ({ ...d, rotation: r }))}
                        data-testid={`grocery-cc-treasure-rot-${r}`}
                        className={`min-h-[36px] rounded-full px-3 font-bold ${
                            (data?.rotation || 'daily') === r
                                ? 'bg-emerald-600 text-white'
                                : 'border border-slate-300 text-foreground dark:border-slate-600'}`}>
                        {t(r === 'daily' ? 'grocery.cc.treasureDaily' : 'grocery.cc.treasureWeekly')}
                    </button>
                ))}
            </div>

            <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-xs">
                    <thead className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        <tr>
                            <th className="px-2 py-1.5 text-left">{t('grocery.cc.treasureItem')}</th>
                            <th className="px-2 py-1.5 text-left">{t('grocery.cc.treasureNote')}</th>
                            <th className="px-2 py-1.5">{t('grocery.cc.treasureOn')}</th>
                            <th className="px-2 py-1.5">{t('grocery.cc.treasureSponsored')}</th>
                            <th className="px-2 py-1.5 text-right">{t('grocery.cc.treasureShown')}</th>
                            <th className="px-2 py-1.5 text-right">{t('grocery.cc.treasureAdded')}</th>
                            <th className="px-2 py-1.5" />
                        </tr>
                    </thead>
                    <tbody>
                        {pool.map((row, i) => {
                            const s = statFor(row.id);
                            return (
                                <tr key={row.id} className="border-t border-slate-100 dark:border-slate-800"
                                    data-testid={`grocery-cc-treasure-row-${row.id}`}>
                                    <td className="px-2 py-1.5">
                                        <Input value={row.catalog_id || row.name || ''}
                                            onChange={(e) => patch(i, row.catalog_id || !row.name ? 'catalog_id' : 'name', e.target.value)}
                                            className="h-9 text-xs"
                                            data-testid={`grocery-cc-treasure-item-${row.id}`} />
                                    </td>
                                    <td className="px-2 py-1.5">
                                        <Input value={row.note || ''} onChange={(e) => patch(i, 'note', e.target.value)}
                                            className="h-9 text-xs"
                                            data-testid={`grocery-cc-treasure-note-${row.id}`} />
                                    </td>
                                    <td className="px-2 py-1.5 text-center">
                                        <button onClick={() => patch(i, 'enabled', !row.enabled)}
                                            data-testid={`grocery-cc-treasure-on-${row.id}`}
                                            className={`min-h-[36px] rounded-full px-3 font-bold ${row.enabled
                                                ? 'bg-emerald-600 text-white' : 'border border-slate-300 dark:border-slate-600'}`}>
                                            {t(row.enabled ? 'grocery.cc.treasureOn' : 'grocery.cc.treasureOff')}
                                        </button>
                                    </td>
                                    <td className="px-2 py-1.5 text-center">
                                        <input type="checkbox" checked={!!row.sponsored}
                                            onChange={(e) => patch(i, 'sponsored', e.target.checked)}
                                            data-testid={`grocery-cc-treasure-paid-${row.id}`}
                                            className="h-5 w-5" />
                                    </td>
                                    <td className="px-2 py-1.5 text-right tabular-nums">
                                        {s?.withheld && !s?.shown ? '—' : (s?.shown ?? 0)}
                                    </td>
                                    <td className="px-2 py-1.5 text-right tabular-nums">
                                        {s?.withheld && !s?.added ? '—' : (s?.added ?? 0)}
                                    </td>
                                    <td className="px-2 py-1.5 text-right">
                                        <button onClick={() => setPool((p) => p.filter((_r, j) => j !== i))}
                                            data-testid={`grocery-cc-treasure-del-${row.id}`}
                                            className="flex h-9 w-9 items-center justify-center rounded-full text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <p className="text-[11px] text-muted-foreground">{t('grocery.cc.treasureCountsNote')}</p>

            <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => setPool((p) => [...p, blank()])}
                    className="min-h-[44px]" data-testid="grocery-cc-treasure-add">
                    <Plus className="mr-1 h-4 w-4" /> {t('grocery.cc.treasureAddRow')}
                </Button>
                <Button onClick={save} disabled={busy} className="min-h-[44px]"
                    data-testid="grocery-cc-treasure-save">
                    {t('common.save', 'Save')}
                </Button>
            </div>
        </div>
    );
}
